import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";
const calculatorKey = "juros-compostos";

export const CompoundInterestInputCard = ({
  setResults,
}: {
  setResults: (value: ResultsType | null) => void;
}) => {
  const isVerified = useIsVerified();
  const [message, setMessage] = useState<string>("");

  const [initialValue, setInitialValue] = useState("");
  const [monthlyValue, setMonthlyValue] = useState("");
  const [interestRate, setInterestRate] = useState("8");
  const [period, setPeriod] = useState("1");
  const [interestPeriodType, setInterestPeriodType] = useState("anual");
  const [periodType, setPeriodType] = useState("ano(s)");

  function clear() {
    setInitialValue("");
    setMonthlyValue("");
    setInterestRate("");
    setPeriod("");
    setInterestPeriodType("anual");
    setPeriodType("ano(s)");
    setResults(null);
  }

  function calcularJurosCompostos() {
    if (!initialValue && !monthlyValue) {
      setMessage("Por favor adicione um valor inicial ou mensal.");
      return;
    }

    if (!interestRate && !period) {
      setMessage("Adicione taxa de juros e período.");
      return;
    }

    if (!period) {
      setMessage("Adicione o período.");
      return;
    }

    if (!interestRate) {
      setMessage("Adicione taxa de juros.");
      return;
    }

    if (!initialValue) {
      setMessage("Adicionar valor inicial.");
      setInitialValue("0");
    }

    if (!monthlyValue) {
      setMessage("Adicionar valor mensal.");
      setMonthlyValue("0");
    }

    let start = Number.parseFloat(
      initialValue.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );

    const initialRate =
      Number.parseFloat(
        interestRate.replace(/[^0-9,.-]+/g, "").replace(",", ".")
      ) / 100;

    const months =
      Number.parseFloat(period.replace(/[^0-9,-]+/g, "").replace(",", ".")) *
      (periodType === "ano(s)" ? 12 : 1);

    let add = Number.parseFloat(
      monthlyValue.replace(/[^0-9,-]+/g, "").replace(",", ".")
    );

    if (isNaN(add)) {
      add = 0;
    }

    if (isNaN(start)) {
      start = 0;
    }

    const rate =
      interestPeriodType === "anual"
        ? Math.pow(1 + initialRate, 1 / 12) - 1
        : initialRate;

    const monthlyTax = [];
    const monthlyResult = [];
    const monthlyInvested = [start];

    let accumulated = 0;
    let accumulated1 = 0;
    let accumulated2 = 0;

    if (start === 0) {
      accumulated = ((Math.pow(1 + rate, months) - 1) / rate) * add;
    } else {
      accumulated1 = start * Math.pow(1 + rate, months);
      accumulated2 = ((Math.pow(1 + rate, months) - 1) / rate) * add;
      accumulated = accumulated1 + accumulated2;
    }

    let totalTaxes = 0;
    let singleMonth = start;
    let singleContribution = start;

    let singleMonthlyTax = 0;
    for (let i = 0; i < months; i++) {
      singleMonthlyTax = singleMonth * rate;
      totalTaxes += singleMonthlyTax;
      singleMonth += singleMonthlyTax + add;
      singleContribution += add;

      monthlyTax.push(singleMonthlyTax);
      monthlyResult.push(totalTaxes);
      monthlyInvested.push(singleContribution);
    }

    setResults({
      accumulated: Number.parseFloat(accumulated.toFixed(2)),
      invested: Number.parseFloat(singleContribution.toFixed(2)),
      interest: Number.parseFloat(totalTaxes.toFixed(2)),
      monthlyResult: monthlyResult.map((item) =>
        Number.parseFloat(item.toFixed(2))
      ),
      monthlyInvested: monthlyInvested.map((item) =>
        Number.parseFloat(item.toFixed(2))
      ),
      monthlyTax: monthlyTax.map((item) => Number.parseFloat(item.toFixed(2))),
    });
  }

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      initialValue,
      monthlyValue,
      interestRate,
      period,
      interestPeriodType,
      periodType,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    setInitialValue((savedData.initialValue as string) || "");
    setMonthlyValue((savedData.monthlyValue as string) || "");
    setInterestRate((savedData.interestRate as string) || "8");
    setPeriod((savedData.period as string) || "1");
    setInterestPeriodType((savedData.interestPeriodType as string) || "anual");
    setPeriodType((savedData.periodType as string) || "ano(s)");
  }, []);

  return (
    <MainCard>
      <MainCardTitle>Simulador de Juros Compostos</MainCardTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
          checkCalculatorUsageLimitAndRedirect(calcularJurosCompostos);
        }}
      >
        <MainCardContent>
          <div>
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="initial-value"
            >
              Valor inicial
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="initial-value"
                placeholder="00,00"
                type="text"
                value={initialValue}
                onChange={(event) => {
                  setInitialValue(sanitizeBrlValue(event));
                }}
              />
            </div>
          </div>
          <div>
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="monthly-value"
            >
              Valor mensal
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="monthly-value"
                placeholder="00,00"
                type="text"
                value={monthlyValue}
                onChange={(event) => {
                  setMonthlyValue(sanitizeBrlValue(event));
                }}
              />
            </div>
          </div>
          <div>
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="interest-rate"
            >
              Taxa de juros
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                %
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="interest-rate"
                placeholder="8,00"
                type="text"
                value={interestRate}
                onChange={(event) => setInterestRate(event.target.value)}
              />
              <select
                className="border-l p-2 bg-white"
                value={interestPeriodType}
                onChange={(event) => setInterestPeriodType(event.target.value)}
              >
                <option>anual</option>
                <option>mensal</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block mb-2 font-medium text-sm" htmlFor="period">
              Período
            </label>
            <div className="flex border">
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="period"
                placeholder="1"
                type="text"
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
              />
              <select
                className="border-l p-2 bg-white"
                value={periodType}
                onChange={(event) => setPeriodType(event.target.value)}
              >
                <option>ano(s)</option>
                <option>mes(es)</option>
              </select>
            </div>
          </div>
        </MainCardContent>
        <div className="flex justify-between">
          <Button
            disabled={!isVerified}
            type="submit"
            variant={"default"}
            className="sm:w-[150px]"
          >
            Calcular
          </Button>

          <div className="flex flex-col sm:flex-row items-center gap-2">
            <a
              href="/calculadoras/calculadora-de-renda"
              className="text-md text-primary text-center "
            >
              Simular retiradas mensais
            </a>

            <Button onClick={clear} variant={"ghost"}>
              Limpar
            </Button>
          </div>
        </div>
      </form>
      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};